import React from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'motion/react';
import { X, FileText, Shield, Info } from 'lucide-react';
import { Button } from './ui/button';
import { ScrollArea } from './ui/scroll-area';
import { FormattedText } from './FormattedText';

interface CommunityRulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  groupName: string;
  rules: string;
  type: 'tribe' | 'forum';
}

export function CommunityRulesModal({ 
  isOpen, 
  onClose, 
  groupName, 
  rules, 
  type 
}: CommunityRulesModalProps) {
  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-1000 flex items-center justify-center p-4">
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
          />

          {/* Modal */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="relative w-full max-w-lg bg-[#FDFBF7] dark:bg-[#1a1a1a] rounded-3xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh] border border-black/5 dark:border-white/10"
          >
            {/* Header */}
            <div className="p-6 border-b border-black/5 dark:border-white/5 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-amber-500" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-black dark:text-white font-outfit">Community Rules</h2>
                  <p className="text-xs text-black/60 dark:text-white/60">Guidelines for {groupName}</p>
                </div>
              </div>
              <motion.button
                whileTap={{ scale: 0.9 }}
                onClick={onClose}
                className="w-10 h-10 rounded-full bg-black/5 dark:bg-white/10 flex items-center justify-center hover:bg-black/10 dark:hover:bg-white/20 transition-colors"
              >
                <X className="w-5 h-5 text-black dark:text-white" />
              </motion.button>
            </div>

            {/* Content */}
            <ScrollArea className="flex-1 p-6">
              <div className="space-y-6">
                {rules ? (
                  <div className="prose prose-sm dark:prose-invert max-w-none">
                    <div className="p-4 rounded-2xl bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5">
                        <FormattedText text={rules} />
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-12 space-y-4">
                    <div className="w-16 h-16 rounded-full bg-black/5 dark:bg-white/5 flex items-center justify-center mx-auto opacity-20">
                      <FileText className="w-8 h-8" />
                    </div>
                    <div className="space-y-1">
                      <p className="text-base font-bold text-black dark:text-white">No Rules Established</p>
                      <p className="text-sm text-black/40 dark:text-white/40 px-6">
                        This {type} hasn't listed specific rules. Basic AfroYap community guidelines apply.
                      </p>
                    </div>
                  </div>
                )}

                <div className="p-4 rounded-xl bg-blue-500/5 border border-blue-500/10 flex items-start gap-3">
                  <Info className="w-4 h-4 text-blue-500 shrink-0 mt-0.5" />
                  <p className="text-[10px] text-blue-600/80 dark:text-blue-400/80 leading-relaxed font-medium">
                    By participating in this {type}, you agree to uphold these rules and maintain a respectful community environment.
                  </p>
                </div>
              </div>
            </ScrollArea>

            {/* Footer */}
            <div className="p-6 pt-2 shrink-0">
               <Button 
                onClick={onClose}
                className="w-full h-12 bg-black dark:bg-white text-white dark:text-black font-bold rounded-2xl hover:opacity-90 shadow-lg"
               >
                 I Understand
               </Button>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>,
    document.body
  );
}
